import { useEffect, useMemo, useRef, useState } from "react";
import { Box, Container, Flex, Theme } from "@radix-ui/themes";
import { I18nProvider } from "@/i18n";
import { ThemeProvider, useTheme } from "@/components/ThemeProvider";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { CategoryTabs } from "@/components/CategoryTabs";
import { ControlStrip } from "@/components/ControlStrip";
import { SeriesChart } from "@/components/SeriesChart";
import { SeriesTable } from "@/components/SeriesTable";
import { LoadingState } from "@/components/LoadingState";
import { EmptyState } from "@/components/EmptyState";
import { ErrorState } from "@/components/ErrorState";
import { useCatalog } from "@/hooks/useCatalog";
import { useObservations } from "@/hooks/useObservations";
import { findCategory, findSeries, pickDefaultSeriesId, resolveInitialState } from "@/lib/catalog";
import { readStateFromUrl, writeStateToUrl } from "@/lib/urlState";
import { isCurrencySeries, type AppState, type CategoryKey } from "@/types";

function AppContent() {
  const catalog = useCatalog();
  const [state, setState] = useState<AppState | null>(null);
  const initialized = useRef(false);

  useEffect(() => {
    if (!catalog.data || initialized.current) return;
    initialized.current = true;
    const resolved = resolveInitialState(readStateFromUrl(), catalog.data);
    setState(resolved);
    writeStateToUrl(resolved);
  }, [catalog.data]);

  const observations = useObservations(state?.series, state?.from ?? "", state?.to ?? "");

  const currentCategorySeries = useMemo(() => {
    if (!catalog.data || !state) return [];
    return findCategory(catalog.data, state.category)?.series ?? [];
  }, [catalog.data, state]);

  const selectedSeriesInfo = useMemo(() => {
    if (!catalog.data || !state) return undefined;
    return findSeries(catalog.data, state.series)?.series;
  }, [catalog.data, state]);

  const sortedData = useMemo(() => {
    if (!observations.data) return [];
    return [...observations.data.data].sort((a, b) => (a.date < b.date ? -1 : 1));
  }, [observations.data]);

  const latestPoint = sortedData.length > 0 ? sortedData[sortedData.length - 1] : null;

  function commit(next: AppState) {
    setState(next);
    writeStateToUrl(next);
  }

  function handleCategoryChange(category: CategoryKey) {
    if (!catalog.data || !state) return;
    commit({ ...state, category, series: pickDefaultSeriesId(catalog.data, category) });
  }

  function handleSeriesChange(seriesId: string) {
    if (!state) return;
    commit({ ...state, series: seriesId });
  }

  function handleApplyRange(from: string, to: string) {
    if (!state) return;
    commit({ ...state, from, to });
  }

  const readoutStatus: "loading" | "ready" | "empty" | "error" = observations.error
    ? "error"
    : observations.loading || !state
      ? "loading"
      : observations.data?.available
        ? "ready"
        : "empty";

  // While a new fetch is in flight, `observations.data` may still hold the
  // previous series' response — don't surface its (now stale) unit/value.
  const displayUnit = observations.loading ? null : (observations.data?.unit ?? null);

  const unitVars =
    displayUnit === "sek_per_unit" && selectedSeriesInfo && isCurrencySeries(selectedSeriesInfo)
      ? { currency: selectedSeriesInfo.iso }
      : undefined;

  const bootstrapping = !state || catalog.loading;

  return (
    <Flex direction="column" className="rk-app">
      <Header />
      <Box asChild className="rk-main">
        <main>
          <Container size="3" px={{ initial: "4", sm: "6" }} py="6">
            {bootstrapping ? (
              <LoadingState />
            ) : catalog.error ? (
              <ErrorState onRetry={catalog.refetch} />
            ) : (
              <Flex direction="column" gap="5">
                <CategoryTabs value={state.category} onChange={handleCategoryChange} />
                <Box className="rk-divider" />
                <ControlStrip
                  series={currentCategorySeries}
                  seriesId={state.series}
                  onSeriesChange={handleSeriesChange}
                  from={state.from}
                  to={state.to}
                  onApplyRange={handleApplyRange}
                  readoutStatus={readoutStatus}
                  latestPoint={latestPoint}
                  unit={displayUnit}
                  unitVars={unitVars}
                />
                <Box className="rk-divider" />
                <Box className="rk-content" aria-live="polite" aria-busy={observations.loading}>
                  {observations.loading ? (
                    <LoadingState />
                  ) : observations.error ? (
                    <ErrorState onRetry={observations.refetch} />
                  ) : observations.data && !observations.data.available ? (
                    <EmptyState />
                  ) : observations.data ? (
                    <Flex direction="column" gap="5">
                      <SeriesChart data={sortedData} unit={observations.data.unit} />
                      <Box className="rk-divider" />
                      <SeriesTable data={sortedData} unit={observations.data.unit} />
                    </Flex>
                  ) : null}
                </Box>
              </Flex>
            )}
          </Container>
        </main>
      </Box>
      <Footer />
    </Flex>
  );
}

function ThemedApp() {
  const { theme } = useTheme();
  return (
    <Theme appearance={theme} accentColor="gold" grayColor="slate" radius="medium">
      <ErrorBoundary>
        <AppContent />
      </ErrorBoundary>
    </Theme>
  );
}

export default function App() {
  return (
    <I18nProvider>
      <ThemeProvider>
        <ThemedApp />
      </ThemeProvider>
    </I18nProvider>
  );
}
